// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/ServicePageLayout.tsx
// 🔧 PURPOSE: La cornice comune delle pagine dei servizi (mockup approvati
//             "Svc..."): prima erano finestre sopra la Home, ora pagine a
//             tutto schermo nello stile della Fuga romantica
//             (pages/RomanticEscape): fondo avorio, titoli in Fraunces,
//             bottoni maiuscoli, e i colori di sempre di ogni servizio.
//
//             Dall'alto:
//             - la testata, in tre forme: con la foto (420px), disegnata
//               (360px, il gradiente del servizio e un disegno) o corta
//               (emergenze, planner). In cima "‹" (44px) e a destra una
//               pillola avorio (HeroPill) o una tessera di vetro (HeroTile);
//               in fondo il filo, l'occhiello, il titolo (38px, 32 nel
//               planner) e il sottotitolo;
//             - il foglio avorio che sale di 20px sulla testata (angoli
//               22px) con il contenuto della pagina;
//             - se serve, la barra fissa in fondo (sopra la barra delle
//               schede), con l'azione principale.
//
//             Il contenitore che scorre è marcato data-scroll-root: tornando
//             indietro si ritrova il posto (useScrollRestoration). Il tema
//             scuro ha le sue classi accanto a ogni colore chiaro; i margini
//             sicuri (notch) stanno in --safe-area-inset-top.
// ============================================

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft } from 'lucide-react';
import { useStatusBarColor } from '../../lib/statusBar';
import { cn } from '../../lib/utils';
import { useServiceBack } from './useServiceBack';

// ---- Il tema di un servizio ---------------------------------------------------------

/** I colori di un servizio: li decide la sua pagina (i colori di sempre). */
export interface ServiceTheme {
  /** Il fondo della testata: le classi del gradiente (sotto la foto finché arriva). */
  hero: string;
  /** Il filo corto sopra l'occhiello, sulla testata (es. 'bg-orange-300'); senza, niente filo. */
  accent?: string;
  /** Il colore dell'occhiello sulla testata (es. 'text-orange-50'). */
  heroEyebrow: string;
  /** Il colore del sottotitolo sulla testata; di base bianco. */
  heroSubtitle?: string;
  /** Il bordo, in chiaro, della barra in basso (es. 'border-[#F1DCCB]'); lo usano anche le card. */
  border: string;
  /** La barra di stato in chiaro (in scuro resta #1a1a1a): espresso sopra le foto, il gradiente sopra i disegni. */
  statusBar: string;
}

/**
 * La testata:
 * - `photo`: la foto (public/images/services) con la velatura del servizio
 *   (sotto il testo bianco) e quella espresso in cima (barra di stato, "‹");
 * - `drawn`: il gradiente del tema, la velatura e un disegno (`art`, in
 *   posizione assoluta: le coordinate partono sotto la barra di stato, come
 *   nel mockup); il disegno sta SOPRA la velatura;
 * - `short`: la testata corta (emergenze, planner), alta quanto il testo;
 *   `art` come sopra (bagliori, icone grandi).
 */
export type ServiceHero =
  | { kind: 'photo'; src: string; alt?: string; /** object-position della foto */ position?: string; veil: string }
  | { kind: 'drawn'; art?: React.ReactNode; veil?: string }
  | { kind: 'short'; art?: React.ReactNode };

// ---- Classi comuni per le pagine ---------------------------------------------------

/** L'occhiello sul foglio avorio; il colore lo aggiunge la pagina (es. 'text-orange-700 dark:text-orange-300'). */
export const SERVICE_EYEBROW = 'm-0 text-[11px] font-extrabold uppercase leading-snug tracking-[0.18em]';
/** La card (angoli 20px); il bordo chiaro lo aggiunge la pagina (theme.border). */
export const SERVICE_CARD = 'rounded-[20px] border bg-nva-card dark:border-stone-800 dark:bg-stone-900';
/** L'avviso (angoli 16px); fondo, bordo e testo li aggiunge la pagina. */
export const SERVICE_NOTICE = 'rounded-2xl border px-3.5 py-3 text-[14px] leading-[1.4]';
/**
 * Il bottone pieno: maiuscolo, spaziato, alto 48px, angoli 12px, va a capo
 * se serve. Il gradiente (testo bianco almeno 4.5:1) lo aggiunge la pagina.
 */
export const SERVICE_BUTTON = cn(
  'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-[18px] py-2.5 text-center text-[12.5px] font-extrabold uppercase leading-[1.3] tracking-button text-white',
  'motion-safe:transition-transform motion-safe:active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-sea focus-visible:ring-offset-2 focus-visible:ring-offset-nva-bone dark:focus-visible:ring-sky-400 dark:focus-visible:ring-offset-stone-950',
);

/** Il tondo avorio del "‹" e la pillola, sopra le foto e i gradienti. */
const ON_HERO = 'bg-nva-bone/95 shadow-[0_4px_14px_rgba(0,0,0,.18)] dark:bg-stone-900/90';
const ON_HERO_FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-nva-espresso';

/** La velatura espresso in cima alle foto, sotto la barra di stato e il "‹". */
const TOP_VEIL = 'linear-gradient(180deg, rgba(42,38,32,.55) 0px, rgba(42,38,32,0) calc(var(--safe-area-inset-top, 0px) + 96px))';

const SAFE_TOP = 'var(--safe-area-inset-top, 0px)';

// ---- Pezzi della testata ----------------------------------------------------------

/**
 * "‹" sulla testata. `disabled`: attenuato e fermo (es. durante l'accesso),
 * ma resta nel giro del tab (aria-disabled, non disabled) e non perde il focus.
 */
export const BackButton: React.FC<{ onClick: () => void; label: string; className?: string; disabled?: boolean }> = ({ onClick, label, className, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-label={label}
    aria-disabled={disabled || undefined}
    className={cn(
      'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-nva-espresso dark:text-stone-100',
      ON_HERO,
      ON_HERO_FOCUS,
      disabled && 'cursor-default opacity-50',
      className,
    )}
  >
    <ChevronLeft className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
  </button>
);

/** La pillola avorio in alto a destra ("A DOMICILIO"): `className` ha il colore del testo, con il suo dark:. */
export const HeroPill: React.FC<{ icon?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ icon, children, className }) => (
  <span
    className={cn(
      'inline-flex min-w-0 max-w-[calc(100%-56px)] items-center gap-1.5 rounded-full px-3 py-[7px] text-[11px] font-extrabold uppercase leading-tight tracking-[0.12em]',
      ON_HERO,
      className,
    )}
  >
    {icon ? <span aria-hidden="true" className="flex flex-shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span> : null}
    <span className="min-w-0">{children}</span>
  </span>
);

/** La tessera di vetro in alto a destra (emergenze, rifiuti a guida): solo decorazione. */
export const HeroTile: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span
    aria-hidden="true"
    className={cn('flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px] bg-white/20 text-white [&>svg]:h-[26px] [&>svg]:w-[26px]', className)}
  >
    {children}
  </span>
);

/** Il tondo della richiesta inviata (76px): `className` ha il gradiente scuro della pagina e l'ombra. */
export const SuccessBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span aria-hidden="true" className={cn('flex h-[76px] w-[76px] flex-shrink-0 items-center justify-center rounded-full text-white', className)}>
    <Check className="h-9 w-9" strokeWidth={3} />
  </span>
);

// ---- La cornice -------------------------------------------------------------------

export interface ServicePageLayoutProps {
  theme: ServiceTheme;
  hero: ServiceHero;
  /** L'occhiello maiuscolo sopra il titolo. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** 'lg' = 38px (di solito), 'md' = 32px (il planner). */
  titleSize?: 'lg' | 'md';
  /** In alto a destra: HeroPill o HeroTile. */
  topRight?: React.ReactNode;
  /** Il nome della pagina per i lettori di schermo; di base il titolo, se è un testo. */
  label?: string;
  /** "‹": di base useServiceBack() (indietro nell'app, o la Home). */
  onBack?: () => void;
  /** L'aria-label di "‹"; di base "Indietro" (common.back). */
  backLabel?: string;
  /** La barra fissa in fondo: il contenuto (la sua disposizione la decide la pagina). */
  bottomBar?: React.ReactNode;
  /** Cambiando, il focus torna sul titolo (una schermata nuova della stessa pagina). */
  focusKey?: string | number;
  /** Classi in più per il foglio avorio (es. un gap diverso). */
  sheetClassName?: string;
  children: React.ReactNode;
}

const ServicePageLayout: React.FC<ServicePageLayoutProps> = ({
  theme,
  hero,
  eyebrow,
  title,
  subtitle,
  titleSize = 'lg',
  topRight,
  label,
  onBack,
  backLabel,
  bottomBar,
  focusKey,
  sheetClassName,
  children,
}) => {
  const { t } = useTranslation();
  const defaultBack = useServiceBack();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useStatusBarColor(theme.statusBar);

  // All'arrivo (e a ogni schermata nuova) il focus va sul titolo: il
  // lettore di schermo annuncia la pagina.
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [focusKey]);

  const tall = hero.kind !== 'short';
  const minHeight = hero.kind === 'photo'
    ? `calc(min(420px, 60vh) + ${SAFE_TOP})`
    : hero.kind === 'drawn'
      ? `calc(360px + ${SAFE_TOP})`
      : undefined;
  const veil = hero.kind === 'photo' ? `${TOP_VEIL}, ${hero.veil}` : hero.kind === 'drawn' ? hero.veil : undefined;
  const art = hero.kind === 'photo' ? null : hero.art;

  return (
    <div
      className="absolute inset-0 flex flex-col bg-nva-bone dark:bg-stone-950"
      style={{ bottom: 'calc(72px + env(safe-area-inset-bottom))', top: 0 }}
    >
      <main
        data-scroll-root
        aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        className="flex-1 overflow-y-auto overflow-x-hidden motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
      >
        <div className="flex min-h-full flex-col">
          <header
            className={cn('relative flex flex-shrink-0 flex-col overflow-hidden px-4 pb-[42px]', theme.hero)}
            style={{ minHeight, paddingTop: `calc(${SAFE_TOP} + 16px)` }}
          >
            {hero.kind === 'photo' ? (
              <img
                src={hero.src}
                alt={hero.alt || ''}
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
                style={hero.position ? { objectPosition: hero.position } : undefined}
              />
            ) : null}
            {veil ? <span aria-hidden="true" className="absolute inset-0" style={{ background: veil }} /> : null}
            {art ? (
              <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0" style={{ top: SAFE_TOP }}>
                {art}
              </div>
            ) : null}

            <div className="relative flex items-center justify-between gap-3">
              <BackButton onClick={onBack || defaultBack} label={backLabel || t('common.back')} />
              {topRight}
            </div>

            <div className={cn('relative flex flex-col px-1', tall ? 'mt-auto pt-5' : 'mt-5', titleSize === 'md' ? 'gap-2' : 'gap-2.5')}>
              {theme.accent ? <span aria-hidden="true" className={cn('block h-0.5 w-10', theme.accent)} /> : null}
              {eyebrow ? (
                <span className={cn('block text-[11px] font-extrabold uppercase leading-snug tracking-[0.2em]', theme.heroEyebrow)}>
                  {eyebrow}
                </span>
              ) : null}
              <h1
                ref={headingRef}
                tabIndex={-1}
                className={cn(
                  'm-0 break-words font-display font-bold text-white outline-none',
                  titleSize === 'md' ? 'text-[32px] leading-[1.05]' : 'text-[38px] leading-[1.04]',
                )}
              >
                {title}
              </h1>
              {subtitle ? (
                <p className={cn('m-0 text-[15px]', theme.heroSubtitle || 'text-white', titleSize === 'md' ? 'leading-[1.4]' : 'leading-[1.45]')}>
                  {subtitle}
                </p>
              ) : null}
            </div>
          </header>

          <div
            className={cn(
              'relative -mt-5 flex flex-grow flex-col gap-3.5 rounded-t-[22px] bg-nva-bone px-4 pb-7 pt-[22px] dark:bg-stone-950',
              sheetClassName,
            )}
          >
            {children}
          </div>
        </div>
      </main>

      {bottomBar ? (
        <div
          className={cn(
            'relative flex-shrink-0 border-t bg-nva-card/95 px-4 pb-4 pt-3 shadow-[0_-8px_24px_rgba(42,38,32,.08)]',
            'dark:border-stone-800 dark:bg-stone-900/95 dark:shadow-none',
            theme.border,
          )}
        >
          {bottomBar}
        </div>
      ) : null}
    </div>
  );
};

export default ServicePageLayout;
